// src/viqai/media-download.js
// ─── Téléchargement des médias produits (§4-5, §44) ────────────────────────
//
// La carte MediaResultCard n'offrait aucun téléchargement : le professionnel
// voyait son showroom et ne pouvait pas le récupérer. C'était le maillon
// manquant de toute la chaîne — préparer des visuels qu'on ne peut pas
// publier ne sert à rien.
//
// Deux chemins, dans cet ordre de préférence :
//   1. la ressource persistante (`/api/viqai/media/:id/download`), authentifiée
//      et nommée correctement côté serveur ;
//   2. le data-URL de la carte, uniquement si le média n'a pas encore d'id
//      (échec de stockage) — §5 : on évite de trimballer du base64 quand une
//      URL contrôlée existe.

import { apiUrl, getAuthHeaders } from "./api";

// Un objet URL non révoqué garde son blob en mémoire jusqu'au rechargement de
// la page. Sur une série de huit photos, cela se voit.
function triggerDownload(blobOrUrl, filename) {
  const isBlob = blobOrUrl instanceof Blob;
  const href = isBlob ? URL.createObjectURL(blobOrUrl) : blobOrUrl;
  const a = document.createElement("a");
  a.href = href;
  a.download = filename || "vehicleiq";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  if (isBlob) setTimeout(() => URL.revokeObjectURL(href), 4000);
}

// Les helpers d'authentification vivent dans App.jsx et sont relayés par
// api.js : pas de seconde source de vérité pour le jeton Firebase.
async function authHeaders() {
  try { return await getAuthHeaders(); } catch (e) { return {}; }
}

/** Télécharge un média unique par son identifiant. */
export async function downloadMedia(mediaId, filename = null) {
  const headers = await authHeaders().catch(() => ({}));
  const res = await fetch(`${apiUrl()}/api/viqai/media/${mediaId}/download`, { headers });
  if (!res.ok) throw new Error("Le téléchargement a échoué.");
  const blob = await res.blob();
  const suggested = filename || decodeURIComponent(
    (res.headers.get("content-disposition") || "").match(/filename="?([^"]+)"?/)?.[1] || ""
  ) || `vehicleiq-${mediaId}`;
  triggerDownload(blob, suggested);
  return true;
}

/** §4 — « Télécharger toutes les photos » : archive produite par le serveur. */
export async function downloadMediaZip(mediaIds = [], { vehicle = {}, mode = null } = {}) {
  if (!mediaIds.length) return false;
  if (mediaIds.length === 1) return downloadMedia(mediaIds[0]);
  const headers = { "Content-Type": "application/json", ...(await authHeaders().catch(() => ({}))) };
  const res = await fetch(`${apiUrl()}/api/viqai/media/zip`, {
    method: "POST", headers,
    body: JSON.stringify({ media_ids: mediaIds, vehicle, mode }),
  });
  if (!res.ok) throw new Error("L'archive n'a pas pu être créée.");
  const blob = await res.blob();
  const name = decodeURIComponent(
    (res.headers.get("content-disposition") || "").match(/filename="?([^"]+)"?/)?.[1] || ""
  ) || "vehicleiq-photos.zip";
  triggerDownload(blob, name);
  return true;
}

/** Repli : le média n'a pas d'id persistant, on descend le data-URL affiché. */
export function downloadDataUrl(dataUrl, filename = "vehicleiq.png") {
  if (!dataUrl) return false;
  triggerDownload(dataUrl, filename);
  return true;
}

/**
 * Choisit automatiquement le bon chemin pour un résultat de carte média.
 * L'appelant n'a pas à savoir si le stockage a réussi.
 */
export function downloadResult(result, { vehicle = {}, mode = null, index = 0 } = {}) {
  if (result?.media_id) return downloadMedia(result.media_id, result.filename);
  const slug = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase();
  const name = ["vehicleiq", slug(vehicle.make), slug(vehicle.model),
    String(vehicle.plaque || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase() || null,
    slug(mode), index + 1].filter(Boolean).join("-") + ".png";
  return Promise.resolve(downloadDataUrl(result?.after || result?.url, name));
}

